import {
  AttendanceStatus,
  CertificateResultStatus,
  OpenClassCertificateType,
  OpenClassRegistration,
  OpenClassSession,
} from '../types/OpenClass';
import { certificateIssued } from './certificateEligibility';

export type SessionAttendee = {
  attendanceStatus: AttendanceStatus;
  besaccCertificateFileName?: string | null;
  certificateIssued: boolean;
  certificateResultStatus: CertificateResultStatus;
  sessionResult: CertificateResultStatus;
  certificateType: OpenClassCertificateType;
  certificateSentAt?: string | null;
  certificateSentTo?: string | null;
  clientCompanyId?: number;
  clientCompanyName?: string;
  email?: string | null;
  name: string;
  registrationId: OpenClassRegistration['id'];
};

export type SessionAttendeeStatus =
  | 'failed'
  | 'notPresent'
  | 'notRecorded'
  // Only a class wide row can hold this, one session is present or it isn't
  | 'partlyPresent'
  | 'passed'
  | 'present';

export const sessionAttendeeStatus = (
  attendee: Pick<SessionAttendee, 'attendanceStatus' | 'sessionResult'>,
): SessionAttendeeStatus => {
  if (attendee.attendanceStatus === 'not_present') return 'notPresent';
  if (attendee.sessionResult === 'passed') return 'passed';
  if (attendee.sessionResult === 'failed') return 'failed';
  if (attendee.attendanceStatus === 'present') return 'present';
  return 'notRecorded';
};

export const sessionAttendees = (
  registrations: OpenClassRegistration[],
  sessionId: OpenClassSession['id'],
): SessionAttendee[] =>
  registrations.flatMap((registration) => {
    const attendance = registration.sessionAttendances?.find(
      (sessionAttendance) => sessionAttendance.sessionId === sessionId,
    );
    if (!attendance) return [];

    return [
      {
        attendanceStatus: attendance.attendanceStatus,
        besaccCertificateFileName: registration.besaccCertificateFileName,
        certificateIssued: certificateIssued(registration),
        certificateResultStatus: registration.certificateResultStatus,
        sessionResult: attendance.result,
        certificateType: registration.certificateType,
        clientCompanyId: registration.attendee.clientCompany?.id,
        clientCompanyName: registration.attendee.clientCompany?.name,
        email: registration.attendee.email,
        name: registration.attendee.name,
        registrationId: registration.id,
        certificateSentAt: registration.certificateSentAt,
        certificateSentTo: registration.certificateSentTo,
      },
    ];
  });

export type SessionAttendeeCsvRow = {
  name: string;
  email: string;
  clientCompany: string;
  status: SessionAttendeeStatus;
  certificateType: string;
};

export const sessionAttendeeCsvColumns: (keyof SessionAttendeeCsvRow)[] = [
  'name',
  'email',
  'clientCompany',
  'status',
  'certificateType',
];

export const sessionAttendeeCsvRows = (
  attendees: SessionAttendee[],
  privateParticipantLabel: string,
  certificateTypeLabel: (type: OpenClassCertificateType) => string,
): SessionAttendeeCsvRow[] =>
  attendees.map((attendee) => ({
    name: attendee.name,
    email: attendee.email ?? '',
    clientCompany: attendee.clientCompanyName || privateParticipantLabel,
    status: sessionAttendeeStatus(attendee),
    certificateType: certificateTypeLabel(attendee.certificateType),
  }));
